import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  FileText,
  Upload,
  Trash2,
  Eye,
  Download,
  Loader2,
  File,
  X,
  ToggleLeft,
  ToggleRight,
  Pencil,
} from 'lucide-react'
import {
  Manual,
  getAllManuals,
  createManual,
  updateManual,
  deleteManual,
  toggleManual,
  formatFileSize,
  fileToBase64,
} from '../../services/manualService'

export default function ManualsPage() {
  const [manuals, setManuals] = useState<Manual[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingManual, setEditingManual] = useState<Manual | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fileName: '',
    fileUrl: '',
    fileSize: 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadManuals()
  }, [])

  const loadManuals = async () => {
    try {
      setLoading(true)
      const response = await getAllManuals()
      setManuals(response.manuals)
    } catch (error) {
      toast.error('Error al cargar los manuales')
    } finally {
      setLoading(false)
    }
  }

  const processFile = async (file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF')
      return
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('El archivo no puede superar 20MB')
      return
    }

    try {
      setUploading(true)
      const base64 = await fileToBase64(file)
      setFormData((prev) => ({
        ...prev,
        fileName: file.name,
        fileUrl: base64,
        fileSize: file.size,
        title: prev.title || file.name.replace('.pdf', ''),
      }))
      toast.success('Archivo cargado correctamente')
    } catch (error) {
      toast.error('Error al cargar el archivo')
    } finally {
      setUploading(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      toast.error('El título es requerido')
      return
    }

    if (!formData.fileUrl && !editingManual) {
      toast.error('Debe seleccionar un archivo PDF')
      return
    }

    try {
      setUploading(true)

      if (editingManual) {
        const updateData: any = {
          title: formData.title,
          description: formData.description || null,
        }
        // Solo actualizar archivo si se seleccionó uno nuevo
        if (formData.fileUrl && formData.fileUrl !== editingManual.fileUrl) {
          updateData.fileName = formData.fileName
          updateData.fileUrl = formData.fileUrl
          updateData.fileSize = formData.fileSize
        }
        await updateManual(editingManual.id, updateData)
        toast.success('Manual actualizado correctamente')
      } else {
        await createManual({
          title: formData.title,
          description: formData.description || undefined,
          fileName: formData.fileName,
          fileUrl: formData.fileUrl,
          fileSize: formData.fileSize,
        })
        toast.success('Manual creado correctamente')
      }

      setShowModal(false)
      resetForm()
      loadManuals()
    } catch (error) {
      toast.error('Error al guardar el manual')
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = (manual: Manual) => {
    setEditingManual(manual)
    setFormData({
      title: manual.title,
      description: manual.description || '',
      fileName: manual.fileName,
      fileUrl: manual.fileUrl,
      fileSize: manual.fileSize,
    })
    setShowModal(true)
  }

  const handleDelete = async (manual: Manual) => {
    if (!confirm(`¿Está seguro de eliminar "${manual.title}"?`)) return

    try {
      await deleteManual(manual.id)
      toast.success('Manual eliminado correctamente')
      loadManuals()
    } catch (error) {
      toast.error('Error al eliminar el manual')
    }
  }

  const handleToggle = async (manual: Manual) => {
    try {
      await toggleManual(manual.id)
      toast.success(manual.isActive ? 'Manual desactivado' : 'Manual activado')
      loadManuals()
    } catch (error) {
      toast.error('Error al cambiar el estado')
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      fileName: '',
      fileUrl: '',
      fileSize: 0,
    })
    setEditingManual(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openNewModal = () => {
    resetForm()
    setShowModal(true)
  }

  const handleDownload = (manual: Manual) => {
    const link = document.createElement('a')
    link.href = manual.fileUrl
    link.download = manual.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manuales de Usuario</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Gestiona los manuales PDF que los empleados pueden descargar
          </p>
        </div>
        <button
          onClick={openNewModal}
          className="btn btn-primary flex items-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Subir Manual
        </button>
      </div>

      {/* Lista de manuales */}
      {manuals.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border">
          <FileText className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay manuales</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Sube el primer manual usando el botón de arriba
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Manual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Archivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tamaño
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-dark-border">
              {manuals.map((manual) => (
                <tr key={manual.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{manual.title}</div>
                        {manual.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {manual.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{manual.fileName}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(manual.fileSize)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        manual.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {manual.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setViewingPdf({ url: manual.fileUrl, title: manual.title })}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Ver PDF"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(manual)}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Descargar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggle(manual)}
                        className={`p-2 rounded-lg ${
                          manual.isActive
                            ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={manual.isActive ? 'Desactivar' : 'Activar'}
                      >
                        {manual.isActive ? (
                          <ToggleRight className="w-5 h-5" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(manual)}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(manual)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingManual ? 'Editar Manual' : 'Nuevo Manual'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Ej: Manual de Usuario - Ordenador"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Descripción opcional del manual"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo PDF {!editingManual && '*'}
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                      isDragging
                        ? 'border-primary bg-primary/10 scale-[1.02]'
                        : formData.fileName
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 hover:border-primary dark:border-gray-600'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    {uploading ? (
                      <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
                    ) : isDragging ? (
                      <div className="space-y-2">
                        <Upload className="w-10 h-10 mx-auto text-primary animate-bounce" />
                        <p className="text-sm font-medium text-primary">
                          Suelta el archivo aquí
                        </p>
                      </div>
                    ) : formData.fileName ? (
                      <div className="space-y-2">
                        <File className="w-8 h-8 mx-auto text-primary" />
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{formData.fileName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(formData.fileSize)}</p>
                        <p className="text-xs text-primary">Click o arrastra para cambiar</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Arrastra un PDF aquí o haz click para seleccionar
                        </p>
                        <p className="text-xs text-gray-400">Máximo 20MB</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingManual ? 'Guardar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver PDF */}
      {viewingPdf && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/70 transition-opacity"
              onClick={() => setViewingPdf(null)}
            />
            <div className="relative bg-white dark:bg-dark-card rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {viewingPdf.title}
                </h2>
                <button
                  onClick={() => setViewingPdf(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={viewingPdf.url}
                  className="w-full h-full"
                  title={viewingPdf.title}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
